/**
 * DOM-Konstruktion für den Planeten-Selektor.
 * Button, Panel-Container und Planeten-Liste.
 */

const B = 'rgba(100,180,255,';
const BRD = B + '0.25)';
const BG = 'linear-gradient(180deg, rgba(8,20,45,0.93), rgba(4,12,28,0.9))';
const GLOW = `0 0 20px ${B}0.1), inset 0 1px 0 ${B}0.12)`;
const SCAN = `repeating-linear-gradient(0deg,transparent,transparent 3px,${B}0.02) 3px,${B}0.02) 4px)`;

const ICONS: Record<string, string> = {
  terran: '\u{1F30D}', gas_giant: '\u{1FA90}', ice: '\u{2744}\uFE0F',
  desert: '\u{1F3DC}\uFE0F', volcanic: '\u{1F30B}', ocean: '\u{1F30A}',
};

const BTN = 76;

export { BTN, ICONS, B, BRD, BG, GLOW, SCAN };

export function buildPlanetButton(
  iconEl: HTMLSpanElement,
): HTMLDivElement {
  const btn = document.createElement('div');
  Object.assign(btn.style, {
    width: BTN + 'px', height: BTN + 'px', borderRadius: '50%', cursor: 'pointer',
    background: BG, border: `2px solid ${BRD}`,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '32px', lineHeight: '1', position: 'relative', zIndex: '2',
    transition: 'border-color 0.2s, box-shadow 0.2s', boxShadow: GLOW,
    touchAction: 'none', overflow: 'hidden',
  });
  Object.assign(iconEl.style, {
    display: 'block', transition: 'transform 0.06s ease-out', pointerEvents: 'none',
  });
  btn.appendChild(iconEl);
  return btn;
}

export function buildPlanetPanel(
  listEl: HTMLDivElement,
): HTMLDivElement {
  const offset = BTN / 2;
  const panel = document.createElement('div');
  Object.assign(panel.style, {
    position: 'absolute', bottom: offset + 'px', left: offset + 'px',
    background: BG, border: `1px solid ${BRD}`, borderRadius: '10px',
    boxShadow: GLOW, minWidth: '210px',
    display: 'none', opacity: '0', transformOrigin: 'bottom left', fontFamily: 'monospace',
  });
  Object.assign(listEl.style, { maxHeight: '220px', overflowY: 'auto' });
  const sep = document.createElement('div');
  sep.style.cssText = `height:1px;background:${BRD};`;
  const footer = document.createElement('div');
  Object.assign(footer.style, {
    padding: '8px 12px 10px', paddingLeft: (BTN - offset + 14) + 'px',
    fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase',
    color: B + '0.45)', backgroundImage: SCAN,
  });
  footer.textContent = 'Meine Planeten';
  panel.append(listEl, sep, footer);
  return panel;
}

export interface PlanetRowData {
  planetType: string;
  axialQ: number;
  axialR: number;
}

export function populatePlanetList(
  listEl: HTMLDivElement,
  planets: readonly PlanetRowData[],
  selectedIdx: number,
  onSelect: (idx: number) => void,
): void {
  listEl.innerHTML = '';
  for (let i = 0; i < planets.length; i++) {
    const p = planets[i];
    const active = i === selectedIdx;
    const row = document.createElement('div');
    Object.assign(row.style, {
      padding: '10px 14px', cursor: 'pointer', fontSize: '13px',
      color: active ? '#bdf' : '#8cf',
      background: active ? B + '0.08)' : 'transparent',
      borderBottom: i < planets.length - 1 ? `1px solid ${B}0.06)` : 'none',
      display: 'flex', alignItems: 'center', gap: '8px',
      transition: 'background 0.15s', backgroundImage: SCAN,
    });
    row.addEventListener('pointerenter', () => {
      if (i !== selectedIdx) row.style.background = B + '0.06)';
    });
    row.addEventListener('pointerleave', () => {
      row.style.background = i === selectedIdx ? B + '0.08)' : 'transparent';
    });
    const icon = ICONS[p.planetType] ?? '\u{1FA90}';
    const label = p.planetType.charAt(0).toUpperCase() + p.planetType.slice(1);
    row.innerHTML = `<span>${icon}</span><span>${label}</span>` +
      `<span style="color:#445a6a;font-size:10px">(${p.axialQ},\u2009${p.axialR})</span>`;
    const idx = i;
    row.addEventListener('click', () => onSelect(idx));
    listEl.appendChild(row);
  }
}
